/**
 * Routes, guards and lazy boundaries (plan F9).
 *
 * Every authenticated route is wrapped in `RequireAuth` **and** its own
 * `RequirePermission` / `RequireFeature`, rather than relying on the sidebar
 * having hidden the link. Someone who bookmarks `/sims` or types it must get
 * the "not available" state, not a crash and not a blank shell — and criterion
 * 11 asserts exactly that path.
 */

import type { ReactElement, ReactNode } from 'react';
import { Navigate, Outlet, Route, Routes } from 'react-router';

import { RequireAuth, RequireFeature, RequirePermission } from './auth/guards.js';
import { AccountPage } from './features/account/AccountPage.js';
import { AuditLogPage } from './features/audit/AuditLogPage.js';
import { BrandingPage } from './features/branding/BrandingPage.js';
import { ExportsPage } from './features/exports/ExportsPage.js';
import { SelectionProvider } from './features/inventory/selection.js';
import { SimListPage } from './features/inventory/SimListPage.js';
import { JobDetailPage } from './features/jobs/JobDetailPage.js';
import { JobListPage } from './features/jobs/JobListPage.js';
import { RoleListPage } from './features/roles/RoleListPage.js';
import { ActivationFlow, SingleSimActivation } from './features/sims/activation/ActivationFlow.js';
import { SimDetailPage } from './features/sims/SimDetailPage.js';
import { SwapFlow } from './features/sims/swap/SwapFlow.js';
import { UserListPage } from './features/users/UserListPage.js';
import { AppShell } from './layout/AppShell.js';
import { ChangePasswordPage } from './pages/ChangePasswordPage.js';
import { DashboardPage } from './pages/DashboardPage.js';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage.js';
import { LoginPage } from './pages/LoginPage.js';
import { AcceptInvitePage, ResetPasswordPage } from './pages/ResetPasswordPage.js';
import { NotFoundPage } from './pages/StatusPages.js';

/** Wraps a screen in the shell and both authorization layers. */
function Guarded({
  permission,
  feature,
  children,
}: {
  permission: Parameters<typeof RequirePermission>[0]['permission'];
  feature?: Parameters<typeof RequireFeature>[0]['feature'];
  children: ReactNode;
}): ReactElement {
  const inner = feature ? <RequireFeature feature={feature}>{children}</RequireFeature> : children;
  return (
    <RequireAuth>
      <AppShell>
        <RequirePermission permission={permission}>{inner}</RequirePermission>
      </AppShell>
    </RequireAuth>
  );
}

/** Holds one selection across the estate and the flows that act on it. */
function SelectionScope(): ReactElement {
  return (
    <SelectionProvider>
      <Outlet />
    </SelectionProvider>
  );
}

export function AppRoutes(): ReactElement {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/dashboard" replace />} />

      {/* Unauthenticated. */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/password/forgot" element={<ForgotPasswordPage />} />
      <Route path="/password/reset" element={<ResetPasswordPage />} />
      <Route path="/invite/accept" element={<AcceptInvitePage />} />

      {/* Authenticated but outside the shell — the shell stays blocked until
          the password is changed (§04.4, E7). */}
      <Route
        path="/password/change"
        element={
          <RequireAuth>
            <ChangePasswordPage />
          </RequireAuth>
        }
      />

      <Route
        path="/dashboard"
        element={
          <Guarded permission="account.read" feature="account.overview">
            <DashboardPage />
          </Guarded>
        }
      />

      {/* Phase 05–07 screens. Routed now so the nav is honest and so a direct
          navigation is permission-checked rather than 404. */}
      {/*
        The estate and the activation flow share **one** `SelectionProvider`, so
        a selection made on the list is still there on the next screen. Nested
        under a layout route rather than wrapped twice: two providers would be
        two independent selections, and the flow would open empty.

        It deliberately does not extend to `/sims/:simRef` or beyond — a
        selection must not outlive the screens that can act on it.
      */}
      <Route element={<SelectionScope />}>
        <Route
          path="/sims"
          element={
            <Guarded permission="sims.read" feature="inventory.baskets">
              <SimListPage />
            </Guarded>
          }
        />
        {/* Before `/sims/:simRef`, or "activate" is read as a SIM ref. */}
        <Route
          path="/sims/activate"
          element={
            <Guarded permission="sims.activate" feature="sims.lifecycle.activate">
              <ActivationFlow />
            </Guarded>
          }
        />
        {/*
          Activating ONE SIM, named by the path — the estate row's Activate
          action and the SIM detail page's.

          The row action used to link to `/sims/activate?sim=<ref>`, which
          nothing read: the operator landed on the picker and had to find the
          SIM they had just clicked. The subject now comes from the URL, the
          shape swap uses, so the screen is identical however it was reached and
          it is linkable.

          Inside the selection scope even though it ignores the selection
          entirely — `ActivationFlow` reads `useSelection()` unconditionally,
          because a hook cannot be conditional, and outside the provider that
          throws. It never writes to it on this path, so nothing leaks into the
          estate's checkboxes.

          Two segments after `/sims`, so it is distinct from the one-segment
          `/sims/activate` above and from `/sims/:simRef` below.
        */}
        <Route
          path="/sims/:simRef/activate"
          element={
            <Guarded permission="sims.activate" feature="sims.lifecycle.activate">
              <SingleSimActivation />
            </Guarded>
          }
        />
      </Route>

      <Route
        path="/sims/:simRef"
        element={
          <Guarded permission="sims.read" feature="inventory.baskets">
            <SimDetailPage />
          </Guarded>
        }
      />
      {/*
        Swap takes the SIM ref from the URL, not from the shared selection. The
        API's payload is `{ existingIccid, newIccid }` — one SIM by
        construction — so a multi-select would be a shape the operation cannot
        honour. Outside `SelectionScope` for the same reason.

        This also makes the flow linkable: SIM detail and the estate row both
        point here, and the screen is identical from either.
      */}
      <Route
        path="/sims/:simRef/swap"
        element={
          <Guarded permission="sims.swap" feature="sims.lifecycle.swap">
            <SwapFlow />
          </Guarded>
        }
      />
      <Route
        path="/jobs"
        element={
          <Guarded permission="jobs.read" feature="jobs.tracking">
            <JobListPage />
          </Guarded>
        }
      />
      <Route
        path="/jobs/:jobRef"
        element={
          <Guarded permission="jobs.read" feature="jobs.tracking">
            <JobDetailPage />
          </Guarded>
        }
      />
      <Route
        path="/exports"
        element={
          <Guarded permission="sims.export" feature="inventory.export">
            <ExportsPage />
          </Guarded>
        }
      />
      <Route
        path="/account"
        element={
          <Guarded permission="account.read" feature="account.overview">
            <AccountPage />
          </Guarded>
        }
      />
      {/*
        The same features the nav declares and the API enforces
        (`requireFeature` on every users/roles/audit route): a bookmarked
        `/users` on an unentitled account must get the "not available" state
        here, not a screen whose every request 403s. Branding stays
        permission-only — the server has no feature for it.
      */}
      <Route
        path="/users"
        element={
          <Guarded permission="users.read" feature="platform.user_management">
            <UserListPage />
          </Guarded>
        }
      />
      <Route
        path="/roles"
        element={
          <Guarded permission="roles.read" feature="platform.user_management">
            <RoleListPage />
          </Guarded>
        }
      />
      <Route
        path="/audit"
        element={
          <Guarded permission="audit.read" feature="platform.audit_log">
            <AuditLogPage />
          </Guarded>
        }
      />
      {/* Permission-only (no feature) — the server gates these routes the
          same way. Edit affordances inside gate on `branding.write`. */}
      <Route
        path="/branding"
        element={
          <Guarded permission="branding.read">
            <BrandingPage />
          </Guarded>
        }
      />

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
